import {
  Button,
  Heading,
  Flex,
  ButtonGroup,
  Tab,
  TabList,
  Tabs,
  Box,
} from '@chakra-ui/react';
import { T } from '@transifex/react';
import { ReactNode, useMemo } from 'react';
import { useExportMembers } from 'features/members/hooks';
import { mdiAccountPlusOutline, mdiTrayArrowDown } from '@mdi/js';
import { useDrawerStateDispatch } from '../../main/context';
import { openDrawer } from '../../main/context/drawer/actionCreators';
import { Feature, useFeatures } from 'lib/featureFlags/features';
import { PortalIcon } from 'features/common/components/PortalIcon';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';

const Header = ({ children }: { children: ReactNode }) => (
  <Flex
    flexDirection={['column', 'column', 'row']}
    justifyContent='space-between'
    marginBottom={12}
    gap={[6, 6, 0]}
  >
    {children}
  </Flex>
);

const TabNavigation = () => {
  const routeMatch = useRouteMatch();
  const history = useHistory();
  const location = useLocation();

  const tabsConfig = useMemo(() => [
    {
      index: 0,
      label: 'Members',
      pathPattern: `${routeMatch.path}/page/`,
      onClick: () => history.push(`${routeMatch.path}/page/1${location.search}`),
    },
    {
      index: 1,
      label: 'Configuration',
      pathPattern: `${routeMatch.path}/configuration`,
      onClick: () => history.push(`${routeMatch.path}/configuration${location.search}`),
    },
  ], [routeMatch.path, location.search, history]);

  const getActiveTabIndex = () => {
    const activeTab = tabsConfig.find(tab => 
      location.pathname.startsWith(tab.pathPattern)
    );

    return activeTab?.index ?? 0;
  };

  const handleTabChange = (index: number) => {
    const selectedTab = tabsConfig[index];
    selectedTab?.onClick();
  };

  return (
    <Box marginBottom={10}>
      <Tabs 
        variant='unstyled' 
        colorScheme='purple' 
        index={getActiveTabIndex()} 
        onChange={handleTabChange}
      >
        <TabList>
          {tabsConfig.map((tab) => (
            <Tab
              key={tab.index}
              _selected={{ color: 'var(--colors-primary-fg)', bg: 'var(--colors-primary-bg)' }}
              fontWeight='bold'
              borderRadius='full'
              height='40px'
              px={6}
              mr={2}
            >
              <T _str={tab.label} />
            </Tab>
          ))}
        </TabList>
      </Tabs>
    </Box>
  );
};

export const MembersHeader = () => {
  const { exportMembers, isLoading: isExportLoading } = useExportMembers();
  const [, drawerDispatch] = useDrawerStateDispatch();
  const features = useFeatures();

  return (
    <>
      <Header>
        <Heading as='h1'>
          <T _str='Team members' />
        </Heading>
        <ButtonGroup
          display='flex'
          flexDirection={['column', 'row']}
          gap={2}
          spacing={0}
        >
          <Feature name={'EXPORT_MEMBERS'}>
            <Button
              data-testid='export'
              variant='outline'
              onClick={async () => await exportMembers()}
              isLoading={isExportLoading}
              loadingText='Export'
              leftIcon={<PortalIcon path={mdiTrayArrowDown} />}
              data-behavior-analytics-id='export members'
            >
              <T _str='Export' />
            </Button>
          </Feature>
          <Feature name='INVITATIONS'>
            <Button
              variant='solid'
              data-testid='create_invite_toggle'
              onClick={() => drawerDispatch(openDrawer('invite-drawer'))}
              leftIcon={<PortalIcon path={mdiAccountPlusOutline} />}
              data-behavior-analytics-id='Invite members button - Members page'
            >
              <T _str='Invite' />
            </Button>
          </Feature>
        </ButtonGroup>
      </Header>
      {features.CONTENT_HUB_TOGGLE && <TabNavigation />}
    </>
  );
};
